import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { api, formatApiError, waLink } from "@/lib/api";
import {
  Building2, CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Clock3, Loader2, MapPin, MessageCircle, Users,
} from "lucide-react";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const SEDI_INFO = {
  Milano: { icon: Building2, address: "Via Pordenone 13, 20132 Milano (MI)", tag: "Sede legale" },
  Gallarate: { icon: Users, address: "Via Scipione Ronchetti 8, 21013 Gallarate (VA)", tag: "Sede operativa" },
};

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDay(iso) {
  return new Date(iso + "T12:00:00").toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

export default function Prenota() {
  const [params] = useSearchParams();
  const initialSede = ["Milano", "Gallarate"].includes(params.get("sede")) ? params.get("sede") : null;
  const [sede, setSede] = useState(initialSede);
  const now = new Date();
  const [view, setView] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState(null);
  const [orario, setOrario] = useState(null);
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", nota: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!sede || !day) return;
    setSlots(null);
    setOrario(null);
    api.get("/appointments/slots", { params: { sede, data: day } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [sede, day]);

  const { offset, daysInMonth } = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    return {
      offset: (first.getDay() + 6) % 7,
      daysInMonth: new Date(view.year, view.month + 1, 0).getDate(),
    };
  }, [view]);

  const todayISO = toISO(new Date());

  const dayISO = (d) => toISO(new Date(view.year, view.month, d));
  const isDisabled = (d) => {
    const date = new Date(view.year, view.month, d);
    return dayISO(d) < todayISO || date.getDay() === 0 || date.getDay() === 6;
  };

  const prevMonth = () => setView((v) => (v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 }));
  const nextMonth = () => setView((v) => (v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 }));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setDone(null); setSede(null); setDay(null); setSlots(null); setOrario(null);
    setForm({ nome: "", cognome: "", email: "", telefono: "", nota: "" });
    setPrivacy(false); setError("");
    window.scrollTo(0, 0);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) {
      setError("Per prenotare devi accettare l'informativa privacy.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/appointments", { ...form, sede, data: day, orario });
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="container-site py-16 lg:py-24 max-w-2xl" data-testid="prenota-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-3xl bg-brand-mist border border-brand-aqua/30 p-8 sm:p-12 text-center"
          data-testid="prenota-success"
        >
          <CheckCircle2 className="w-16 h-16 text-brand-aqua mx-auto" />
          <h1 className="mt-5 text-3xl font-extrabold text-brand-ink">Colloquio prenotato!</h1>
          <div className="mt-6 rounded-2xl bg-white border border-slate-100 p-6 text-left space-y-3">
            <p className="flex items-center gap-3 font-bold text-brand-ink text-lg">
              <MapPin className="w-5 h-5 text-brand-blue" />
              {done.appointment.sede} — {SEDI_INFO[done.appointment.sede].address}
            </p>
            <p className="flex items-center gap-3 font-bold text-brand-ink text-lg capitalize">
              <CalendarDays className="w-5 h-5 text-brand-blue" />
              {formatDay(done.appointment.data)}
            </p>
            <p className="flex items-center gap-3 font-bold text-brand-ink text-lg">
              <Clock3 className="w-5 h-5 text-brand-blue" />
              Ore {done.appointment.orario}
            </p>
          </div>
          <p className="mt-5 text-slate-600">
            Il colloquio è gratuito e riservato. Se non puoi venire, avvisaci su WhatsApp così
            liberiamo il posto per un'altra persona.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={waLink(`Buongiorno, ho prenotato un colloquio a ${done.appointment.sede} il ${formatDay(done.appointment.data)} alle ${done.appointment.orario}.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-aqua"
              data-testid="prenota-success-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Conferma su WhatsApp
            </a>
            <button onClick={reset} className="btn-outline" data-testid="prenota-new-button">
              Prenota un altro colloquio
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container-site py-16 lg:py-24 max-w-3xl" data-testid="prenota-page">
      <Reveal className="text-center">
        <span className="eyebrow justify-center">Sportello di ascolto</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-ink">
          Prenota un colloquio gratuito.
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          Vieni a trovarci in sede a Milano o Gallarate: un operatore ti ascolterà e ti spiegherà
          le strade possibili per la tua situazione. Gratis e senza impegno.
        </p>
      </Reveal>

      {/* Passo 1: sede */}
      <Reveal className="mt-12">
        <h2 className="flex items-center gap-3 text-xl font-extrabold text-brand-ink">
          <span className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center text-base">1</span>
          Scegli la sede
        </h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.entries(SEDI_INFO).map(([nome, info]) => (
            <button
              key={nome}
              onClick={() => { setSede(nome); setDay(null); setOrario(null); }}
              data-testid={`prenota-sede-${nome.toLowerCase()}`}
              className={`text-left rounded-2xl border-2 p-6 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 ${
                sede === nome
                  ? "border-brand-blue bg-brand-mist shadow-lg shadow-brand-blue/10"
                  : "border-slate-200 bg-white hover:border-brand-blue/40"
              }`}
            >
              <info.icon className={`w-7 h-7 ${sede === nome ? "text-brand-blue" : "text-slate-400"}`} />
              <p className="mt-3 font-extrabold text-lg text-brand-ink">{nome}</p>
              <p className="text-sm font-semibold text-brand-aqua">{info.tag}</p>
              <p className="mt-1 text-sm text-slate-500">{info.address}</p>
            </button>
          ))}
        </div>
      </Reveal>

      {/* Passo 2: giorno */}
      {sede && (
        <Reveal className="mt-10">
          <h2 className="flex items-center gap-3 text-xl font-extrabold text-brand-ink">
            <span className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center text-base">2</span>
            Scegli il giorno <span className="text-sm font-semibold text-slate-400">(lun - ven)</span>
          </h2>
          <div className="mt-5 rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-6" data-testid="prenota-calendar">
            <div className="flex items-center justify-between mb-4">
              <button onClick={prevMonth} aria-label="Mese precedente" data-testid="calendar-prev-button" className="p-2.5 rounded-xl hover:bg-brand-mist transition-colors">
                <ChevronLeft className="w-6 h-6 text-brand-blue" />
              </button>
              <p className="font-extrabold text-lg text-brand-ink" data-testid="calendar-month-label">
                {MESI[view.month]} {view.year}
              </p>
              <button onClick={nextMonth} aria-label="Mese successivo" data-testid="calendar-next-button" className="p-2.5 rounded-xl hover:bg-brand-mist transition-colors">
                <ChevronRight className="w-6 h-6 text-brand-blue" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1.5 text-center">
              {GIORNI.map((g) => (
                <span key={g} className="text-xs font-bold uppercase tracking-wider text-slate-400 py-2">{g}</span>
              ))}
              {Array.from({ length: offset }).map((_, i) => <span key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const d = i + 1;
                const iso = dayISO(d);
                const disabled = isDisabled(d);
                const selected = day === iso;
                return (
                  <button
                    key={d}
                    disabled={disabled}
                    onClick={() => setDay(iso)}
                    data-testid={`prenota-day-${iso}`}
                    className={`h-12 rounded-xl font-bold text-base transition-colors ${
                      selected
                        ? "bg-brand-blue text-white shadow-md shadow-brand-blue/30"
                        : disabled
                          ? "text-slate-300 cursor-not-allowed"
                          : "text-brand-ink hover:bg-brand-mist"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
      )}

      {/* Passo 3: orario */}
      {sede && day && (
        <Reveal className="mt-10">
          <h2 className="flex items-center gap-3 text-xl font-extrabold text-brand-ink">
            <span className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center text-base">3</span>
            Scegli l'orario <span className="text-sm font-semibold text-brand-aqua capitalize">— {formatDay(day)}</span>
          </h2>
          <div className="mt-5 rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-6" data-testid="prenota-slots">
            {slots === null ? (
              <p className="py-6 text-center text-slate-500 font-semibold">Caricamento disponibilità…</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {slots.map((s) => (
                  <button
                    key={s.orario}
                    disabled={!s.disponibile}
                    onClick={() => setOrario(s.orario)}
                    data-testid={`prenota-slot-${s.orario.replace(":", "")}`}
                    className={`h-14 rounded-xl font-bold text-lg transition-colors ${
                      orario === s.orario
                        ? "bg-brand-aqua text-white shadow-md shadow-brand-aqua/30"
                        : s.disponibile
                          ? "bg-brand-mist text-brand-ink hover:bg-brand-aqua hover:text-white"
                          : "bg-slate-50 text-slate-300 line-through cursor-not-allowed"
                    }`}
                  >
                    {s.orario}
                  </button>
                ))}
              </div>
            )}
            <p className="mt-5 flex items-start gap-2.5 rounded-xl bg-brand-mist px-4 py-3 text-sm font-semibold text-brand-ink" data-testid="prenota-orari-nota">
              <Clock3 className="w-5 h-5 text-brand-aqua shrink-0 mt-0.5" />
              <span>
                Orari dello sportello: lunedì - venerdì, 9:00 - 13:00.{" "}
                <a
                  href={waLink("Buongiorno, avrei bisogno di un colloquio in un orario diverso dallo sportello (9:00-13:00). Possiamo fissare un appuntamento specifico?")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-blue underline underline-offset-2"
                  data-testid="prenota-orari-whatsapp-link"
                >
                  Per altri orari, richiedi un appuntamento specifico su WhatsApp.
                </a>
              </span>
            </p>
          </div>
        </Reveal>
      )}

      {/* Passo 4: dati */}
      {sede && day && orario && (
        <Reveal className="mt-10">
          <h2 className="flex items-center gap-3 text-xl font-extrabold text-brand-ink">
            <span className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center text-base">4</span>
            I tuoi dati
          </h2>
          <form onSubmit={submit} className="mt-5 rounded-3xl border border-slate-100 bg-white shadow-xl shadow-brand-blue/5 p-8" data-testid="prenota-form">
            <div className="rounded-2xl bg-brand-mist px-5 py-4 mb-6 flex flex-wrap gap-x-6 gap-y-1 font-semibold text-brand-ink">
              <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-blue" />{sede}</span>
              <span className="inline-flex items-center gap-2 capitalize"><CalendarDays className="w-4 h-4 text-brand-blue" />{formatDay(day)}</span>
              <span className="inline-flex items-center gap-2"><Clock3 className="w-4 h-4 text-brand-blue" />{orario}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="pr-nome" className="label-field">Nome *</label>
                <input id="pr-nome" required value={form.nome} onChange={set("nome")} className="input-field" data-testid="prenota-nome-input" />
              </div>
              <div>
                <label htmlFor="pr-cognome" className="label-field">Cognome *</label>
                <input id="pr-cognome" required value={form.cognome} onChange={set("cognome")} className="input-field" data-testid="prenota-cognome-input" />
              </div>
              <div>
                <label htmlFor="pr-email" className="label-field">Email *</label>
                <input id="pr-email" type="email" required value={form.email} onChange={set("email")} className="input-field" data-testid="prenota-email-input" />
              </div>
              <div>
                <label htmlFor="pr-telefono" className="label-field">Telefono *</label>
                <input id="pr-telefono" type="tel" required value={form.telefono} onChange={set("telefono")} className="input-field" data-testid="prenota-telefono-input" />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="pr-nota" className="label-field">Vuoi dirci qualcosa in anticipo? <span className="font-normal text-slate-400">(facoltativo)</span></label>
              <textarea id="pr-nota" rows={3} value={form.nota} onChange={set("nota")} className="input-field h-auto py-4 resize-y" placeholder="Es. preferisco parlare di persona perché non uso bene il telefono…" data-testid="prenota-nota-textarea" />
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1.5 w-5 h-5 accent-brand-blue" data-testid="prenota-privacy-checkbox" />
              <span className="text-slate-600">
                Ho letto{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue font-semibold underline underline-offset-2" data-testid="prenota-privacy-link">
                  l'informativa privacy
                </a>{" "}
                e acconsento al trattamento dei miei dati. *
              </span>
            </label>
            {error && (
              <p className="mt-4 rounded-xl bg-red-50 text-red-700 font-semibold px-4 py-3" role="alert" data-testid="prenota-error">{error}</p>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full mt-7 disabled:opacity-60" data-testid="prenota-submit-button">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CalendarDays className="w-5 h-5" />}
              {loading ? "Prenotazione in corso…" : "Conferma la prenotazione gratuita"}
            </button>
          </form>
        </Reveal>
      )}
    </div>
  );
}
